/**
 * Interactive Seating Chart - Complete Rebuild
 * Elvis Aloha from Hawaii Collection
 * 
 * ARCHITECTURE:
 * - Base Layer: 360-Stage venue reference (the correct Aloha configuration)
 * - Section Layer: Complete section topology with all floor sections
 * - Historical Zone Layer: Golden Circle (AA, BB, CC)
 * - Stage Overlay Layer: Aloha stage reconstruction
 * - Ticket Layer: Known ticket markers at exact coordinates
 * 
 * FLOOR SECTIONS (from 360-Stage chart):
 * Closest to stage: A, B, C, D, E, F, G
 * Other floor: J, K, L, M, N, P, S, T
 * Double-letter: AA, BB, CC, DD, EE, FF, GG, HH, JJ, KK, LL, NN, PP, SS
 * 
 * GOLDEN CIRCLE = AA + BB + CC (approximately 200 major contributors)
 * SECTION L = Floor section behind/affected by stage
 * SECTION H = Does NOT exist on 360-stage layout (inventory may need review)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================================
// CHART CONFIGURATION - 360-Stage Layout
// ============================================================================

const CHART_WIDTH = 2550;
const CHART_HEIGHT = 3300;

// Historical zones
const HISTORICAL_ZONES = {
  'golden-circle': {
    id: 'golden-circle',
    name: 'Golden Circle',
    sections: ['AA', 'BB', 'CC'],
    description: 'Special seating for approximately 200 major contributors to the benefit concert',
    historicalAllocation: 200,
    mappingStatus: 'archive_supported_reconstruction',
    color: 'rgba(201, 169, 98, 0.15)',
    borderColor: '#C9A962'
  }
};

// Section definitions for 360-Stage layout
// Coordinates are approximate and will be refined via owner mapping tool
const SECTIONS = {
  // Golden Circle sections (closest to stage)
  'AA': {
    id: 'AA',
    label: 'Section AA',
    level: 'floor',
    historicalZone: 'golden-circle',
    // Approximate polygon coordinates on 360-stage chart
    bounds: { x: 1200, y: 1400, width: 150, height: 200 },
    stageRelationship: 'front_of_stage',
    mappingStatus: 'approximate'
  },
  'BB': {
    id: 'BB',
    label: 'Section BB',
    level: 'floor',
    historicalZone: 'golden-circle',
    bounds: { x: 1050, y: 1350, width: 200, height: 250 },
    stageRelationship: 'front_of_stage',
    mappingStatus: 'approximate'
  },
  'CC': {
    id: 'CC',
    label: 'Section CC',
    level: 'floor',
    historicalZone: 'golden-circle',
    bounds: { x: 1300, y: 1350, width: 200, height: 250 },
    stageRelationship: 'front_of_stage',
    mappingStatus: 'approximate'
  },
  // Section H - NOTE: May not exist on 360-stage layout
  // The inventory shows 44 tickets from "Section H" - needs investigation
  'H': {
    id: 'H',
    label: 'Section H',
    level: 'floor',
    bounds: { x: 800, y: 1500, width: 200, height: 200 },
    stageRelationship: 'side_of_stage',
    mappingStatus: 'needs_verification',
    note: 'Section H not found on 360-stage reference. May use different naming in 1973.'
  },
  // Section L - behind the stage
  'L': {
    id: 'L',
    label: 'Section L',
    level: 'floor',
    bounds: { x: 1100, y: 900, width: 350, height: 300 },
    stageRelationship: 'behind_stage',
    alohaAvailability: 'unavailable',
    mappingStatus: 'approximate',
    description: 'Located behind the Aloha stage. Tickets printed but seats not used for audience.'
  },
  // Other floor sections from 360-stage layout
  'A': { id: 'A', label: 'Section A', level: 'floor', bounds: { x: 950, y: 1650, width: 150, height: 150 }, stageRelationship: 'front_of_stage' },
  'B': { id: 'B', label: 'Section B', level: 'floor', bounds: { x: 1100, y: 1700, width: 150, height: 150 }, stageRelationship: 'front_of_stage' },
  'C': { id: 'C', label: 'Section C', level: 'floor', bounds: { x: 1250, y: 1650, width: 150, height: 150 }, stageRelationship: 'front_of_stage' },
  'D': { id: 'D', label: 'Section D', level: 'floor', bounds: { x: 1400, y: 1550, width: 150, height: 150 }, stageRelationship: 'side_of_stage' },
  'E': { id: 'E', label: 'Section E', level: 'floor', bounds: { x: 1500, y: 1400, width: 150, height: 150 }, stageRelationship: 'side_of_stage' },
  'F': { id: 'F', label: 'Section F', level: 'floor', bounds: { x: 1550, y: 1250, width: 150, height: 150 }, stageRelationship: 'side_of_stage' },
  'G': { id: 'G', label: 'Section G', level: 'floor', bounds: { x: 1500, y: 1100, width: 150, height: 150 }, stageRelationship: 'side_of_stage' },
  'J': { id: 'J', label: 'Section J', level: 'floor', bounds: { x: 750, y: 1100, width: 150, height: 150 }, stageRelationship: 'side_of_stage' },
  'K': { id: 'K', label: 'Section K', level: 'floor', bounds: { x: 700, y: 1250, width: 150, height: 150 }, stageRelationship: 'side_of_stage' },
  'M': { id: 'M', label: 'Section M', level: 'floor', bounds: { x: 1400, y: 1000, width: 150, height: 150 }, stageRelationship: 'behind_stage' },
  'N': { id: 'N', label: 'Section N', level: 'floor', bounds: { x: 850, y: 1000, width: 150, height: 150 }, stageRelationship: 'behind_stage' },
  'P': { id: 'P', label: 'Section P', level: 'floor', bounds: { x: 1275, y: 850, width: 150, height: 150 }, stageRelationship: 'behind_stage' },
  'S': { id: 'S', label: 'Section S', level: 'floor', bounds: { x: 950, y: 850, width: 150, height: 150 }, stageRelationship: 'behind_stage' },
  'T': { id: 'T', label: 'Section T', level: 'floor', bounds: { x: 1100, y: 800, width: 150, height: 150 }, stageRelationship: 'behind_stage' },
};

// Sections with known tickets in the inventory
const INVENTORY_SECTIONS = ['AA', 'BB', 'H', 'L'];

// Colors
const COLORS = {
  sectionDefault: 'transparent',
  sectionHover: 'rgba(201, 169, 98, 0.25)',
  sectionActive: 'rgba(201, 169, 98, 0.4)',
  sectionOutline: 'rgba(201, 169, 98, 0.6)',
  goldenCircle: 'rgba(255, 215, 0, 0.1)',
  goldenCircleBorder: '#C9A962',
  behindStage: 'rgba(100, 100, 100, 0.3)',
  behindStageHatch: 'rgba(80, 80, 80, 0.2)',
  stageOverlay: 'rgba(30, 30, 30, 0.7)',
  ticketMarker: '#C9A962',
  ticketMarkerBorder: 'white'
};

// ============================================================================
// ZOOM LEVELS
// ============================================================================

const ZOOM_LEVELS = {
  arena: { scale: 0.22, name: 'Arena Overview' },
  zone: { scale: 0.6, name: 'Zone View' },
  section: { scale: 1.2, name: 'Section View' },
  row: { scale: 2.5, name: 'Row View' },
  seat: { scale: 5, name: 'Seat View' }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const InteractiveSeatingChart = ({ embedded = false }) => {
  // View state
  const [layoutView, setLayoutView] = useState('full-arena'); // 'full-arena' | 'aloha-stage'
  const [zoomLevel, setZoomLevel] = useState('arena');
  const [selectedSection, setSelectedSection] = useState(null);
  const [hoveredSection, setHoveredSection] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [hoveredTicket, setHoveredTicket] = useState(null);
  
  // Overlay toggles
  const [showGoldenCircle, setShowGoldenCircle] = useState(true);
  const [showStageAffected, setShowStageAffected] = useState(true);
  
  // Data
  const [tickets, setTickets] = useState([]);
  const [sectionCounts, setSectionCounts] = useState({});
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Refs
  const containerRef = useRef(null);
  const transformRef = useRef(null);
  
  // Fetch tickets
  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/seat-map/tickets?per_page=500`);
        const data = await res.json();
        const ticketList = data.tickets || [];
        setTickets(ticketList);
        
        // Count by section
        const counts = {};
        ticketList.forEach(t => {
          if (t.section) {
            counts[t.section] = (counts[t.section] || 0) + 1;
          }
        });
        setSectionCounts(counts);
      } catch (err) {
        console.error('Failed to fetch tickets:', err);
      }
    };
    fetchTickets();
  }, []);
  
  // Zoom to section
  const zoomToSection = useCallback((sectionId) => {
    const section = SECTIONS[sectionId];
    if (!section || !transformRef.current) return;
    
    setSelectedSection(sectionId);
    setZoomLevel('section');
    
    // Calculate center point
    const centerX = section.bounds.x + section.bounds.width / 2;
    const centerY = section.bounds.y + section.bounds.height / 2;
    
    // Use the transform API to zoom
    transformRef.current.setTransform(
      -centerX * ZOOM_LEVELS.section.scale + (containerRef.current?.offsetWidth || 800) / 2,
      -centerY * ZOOM_LEVELS.section.scale + (containerRef.current?.offsetHeight || 600) / 2,
      ZOOM_LEVELS.section.scale,
      300
    );
  }, []);
  
  // Zoom to Golden Circle (all three sections)
  const zoomToGoldenCircle = useCallback(() => {
    if (!transformRef.current) return;
    
    // Calculate bounds encompassing AA, BB, CC
    const minX = Math.min(SECTIONS.AA.bounds.x, SECTIONS.BB.bounds.x, SECTIONS.CC.bounds.x);
    const minY = Math.min(SECTIONS.AA.bounds.y, SECTIONS.BB.bounds.y, SECTIONS.CC.bounds.y);
    const maxX = Math.max(
      SECTIONS.AA.bounds.x + SECTIONS.AA.bounds.width,
      SECTIONS.BB.bounds.x + SECTIONS.BB.bounds.width,
      SECTIONS.CC.bounds.x + SECTIONS.CC.bounds.width
    );
    const maxY = Math.max(
      SECTIONS.AA.bounds.y + SECTIONS.AA.bounds.height,
      SECTIONS.BB.bounds.y + SECTIONS.BB.bounds.height,
      SECTIONS.CC.bounds.y + SECTIONS.CC.bounds.height
    );
    
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    
    setSelectedSection('golden-circle');
    setZoomLevel('zone');
    
    transformRef.current.setTransform(
      -centerX * ZOOM_LEVELS.zone.scale + (containerRef.current?.offsetWidth || 800) / 2,
      -centerY * ZOOM_LEVELS.zone.scale + (containerRef.current?.offsetHeight || 600) / 2,
      ZOOM_LEVELS.zone.scale,
      300
    );
  }, []);
  
  // Reset to full arena
  const resetView = useCallback(() => {
    setSelectedSection(null);
    setZoomLevel('arena');
    if (transformRef.current) {
      transformRef.current.resetTransform(300);
    }
  }, []);
  
  // Get section tooltip content
  const getSectionTooltip = (sectionId) => {
    const section = SECTIONS[sectionId];
    if (!section) return null;
    
    const count = sectionCounts[sectionId] || 0;
    const isGoldenCircle = section.historicalZone === 'golden-circle';
    const isBehindStage = section.stageRelationship === 'behind_stage';
    
    return {
      title: section.label,
      subtitle: isGoldenCircle ? 'Golden Circle' : null,
      count,
      stageInfo: layoutView === 'aloha-stage' && isBehindStage 
        ? 'Behind the reconstructed Aloha stage. Not used for concert audience.'
        : null,
      note: section.note
    };
  };
  
  // Calculate Golden Circle total
  const goldenCircleTotal = (sectionCounts['AA'] || 0) + (sectionCounts['BB'] || 0) + (sectionCounts['CC'] || 0);
  
  return (
    <div
      ref={containerRef}
      data-testid="interactive-seating-chart"
      style={{
        position: 'relative',
        width: '100%',
        height: embedded ? '700px' : 'calc(100vh - 100px)',
        minHeight: '600px',
        background: '#f8f6f1', // Warm ivory to match site
        borderRadius: embedded ? '12px' : 0,
        overflow: 'hidden'
      }}
    >
      {/* ================================================================
          TOOLBAR
          ================================================================ */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: 'rgba(255,255,255,0.95)',
        borderBottom: '1px solid #e0e0e0',
        padding: '12px 20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        alignItems: 'center'
      }}>
        {/* Layout Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#666', fontWeight: 600 }}>Layout:</span>
          <button
            onClick={() => setLayoutView('full-arena')}
            data-testid="view-full-arena"
            style={{
              padding: '8px 14px',
              background: layoutView === 'full-arena' ? '#1a1a1a' : 'white',
              color: layoutView === 'full-arena' ? 'white' : '#333',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Full Arena
          </button>
          <button
            onClick={() => setLayoutView('aloha-stage')}
            data-testid="view-aloha-stage"
            style={{
              padding: '8px 14px',
              background: layoutView === 'aloha-stage' ? '#C9A962' : 'white',
              color: layoutView === 'aloha-stage' ? 'white' : '#333',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Aloha Stage
          </button>
        </div>
        
        {/* Overlay Toggles */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', borderLeft: '1px solid #ddd', paddingLeft: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showGoldenCircle}
              onChange={(e) => setShowGoldenCircle(e.target.checked)}
              style={{ accentColor: '#C9A962' }}
            />
            Golden Circle
          </label>
        </div>
        
        {/* Section Shortcuts */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', borderLeft: '1px solid #ddd', paddingLeft: '16px' }}>
          <span style={{ fontSize: '12px', color: '#666' }}>Jump to:</span>
          <button
            onClick={zoomToGoldenCircle}
            data-testid="jump-golden-circle"
            style={{
              padding: '6px 12px',
              background: selectedSection === 'golden-circle' ? '#C9A962' : '#fff8e7',
              color: selectedSection === 'golden-circle' ? 'white' : '#8a7842',
              border: '1px solid #C9A962',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Golden Circle ({goldenCircleTotal})
          </button>
          {INVENTORY_SECTIONS.map(sectionId => (
            <button
              key={sectionId}
              onClick={() => zoomToSection(sectionId)}
              data-testid={`jump-${sectionId}`}
              style={{
                padding: '6px 12px',
                background: selectedSection === sectionId ? '#C9A962' : 'white',
                color: selectedSection === sectionId ? 'white' : '#333',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              {sectionId} ({sectionCounts[sectionId] || 0})
            </button>
          ))}
        </div>
        
        {/* Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
          {selectedSection && (
            <button
              onClick={resetView}
              data-testid="back-to-arena"
              style={{
                padding: '6px 12px',
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              ← Full Arena
            </button>
          )}
          <button
            onClick={resetView}
            data-testid="reset-view"
            style={{
              padding: '6px 12px',
              background: 'white',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Reset
          </button>
        </div>
      </div>
      
      {/* ================================================================
          CHART CONTAINER
          ================================================================ */}
      <div style={{ paddingTop: '60px', height: '100%' }}>
        <TransformWrapper
          ref={transformRef}
          initialScale={ZOOM_LEVELS.arena.scale}
          minScale={0.15}
          maxScale={8}
          centerOnInit
          wheel={{ step: 0.1 }}
          pinch={{ step: 5 }}
          doubleClick={{ mode: 'zoomIn' }}
          limitToBounds={false}
        >
          <TransformComponent
            wrapperStyle={{ width: '100%', height: '100%' }}
            contentStyle={{ width: CHART_WIDTH, height: CHART_HEIGHT }}
          >
            <div style={{ position: 'relative', width: CHART_WIDTH, height: CHART_HEIGHT }}>
              
              {/* Layer 1: Base Chart Image (360-Stage Layout) */}
              <img
                src="/assets/charts/arena-360-stage-1.png"
                alt="Neal S. Blaisdell Arena - 360 Stage Layout"
                onLoad={() => setImageLoaded(true)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
              
              {/* Layer 2: SVG Interaction Overlay */}
              <svg
                viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none'
                }}
              >
                {/* Defs for patterns */}
                <defs>
                  <pattern id="behindStageHatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                    <line x1="0" y1="0" x2="0" y2="8" stroke="rgba(100,100,100,0.3)" strokeWidth="1" />
                  </pattern>
                </defs>
                
                {/* Golden Circle Zone Outline (when enabled) */}
                {showGoldenCircle && (
                  <g id="golden-circle-zone" style={{ pointerEvents: 'auto' }}>
                    <rect
                      x={Math.min(SECTIONS.AA.bounds.x, SECTIONS.BB.bounds.x, SECTIONS.CC.bounds.x) - 20}
                      y={Math.min(SECTIONS.AA.bounds.y, SECTIONS.BB.bounds.y, SECTIONS.CC.bounds.y) - 20}
                      width={Math.max(
                        SECTIONS.AA.bounds.x + SECTIONS.AA.bounds.width,
                        SECTIONS.BB.bounds.x + SECTIONS.BB.bounds.width,
                        SECTIONS.CC.bounds.x + SECTIONS.CC.bounds.width
                      ) - Math.min(SECTIONS.AA.bounds.x, SECTIONS.BB.bounds.x, SECTIONS.CC.bounds.x) + 40}
                      height={Math.max(
                        SECTIONS.AA.bounds.y + SECTIONS.AA.bounds.height,
                        SECTIONS.BB.bounds.y + SECTIONS.BB.bounds.height,
                        SECTIONS.CC.bounds.y + SECTIONS.CC.bounds.height
                      ) - Math.min(SECTIONS.AA.bounds.y, SECTIONS.BB.bounds.y, SECTIONS.CC.bounds.y) + 40}
                      fill={COLORS.goldenCircle}
                      stroke={COLORS.goldenCircleBorder}
                      strokeWidth={3}
                      strokeDasharray="12 6"
                      rx={8}
                      onClick={zoomToGoldenCircle}
                      style={{ cursor: 'pointer' }}
                    />
                    <text
                      x={1175}
                      y={1330}
                      textAnchor="middle"
                      fill="#8a7842"
                      fontSize="28"
                      fontWeight="600"
                      fontFamily="'Cormorant Garamond', serif"
                      style={{ pointerEvents: 'none' }}
                    >
                      GOLDEN CIRCLE
                    </text>
                  </g>
                )}
                
                {/* Aloha Stage Overlay (when enabled) */}
                {layoutView === 'aloha-stage' && (
                  <g id="aloha-stage-overlay">
                    {/* Behind-stage zone */}
                    <rect
                      x={900}
                      y={700}
                      width={750}
                      height={400}
                      fill="url(#behindStageHatch)"
                      stroke="#666"
                      strokeWidth={2}
                      strokeDasharray="8 4"
                    />
                    <text x={1275} y={870} textAnchor="middle" fill="#666" fontSize="18" fontWeight="600">
                      Behind Stage
                    </text>
                    <text x={1275} y={895} textAnchor="middle" fill="#888" fontSize="14">
                      Not used for audience
                    </text>
                    
                    {/* Main stage */}
                    <rect
                      x={1050}
                      y={1050}
                      width={450}
                      height={250}
                      fill={COLORS.stageOverlay}
                      stroke="#C9A962"
                      strokeWidth={3}
                      rx={4}
                    />
                    <text x={1275} y={1185} textAnchor="middle" fill="white" fontSize="32" fontWeight="600">
                      STAGE
                    </text>
                    <text x={1275} y={1220} textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="14">
                      Research Reconstruction
                    </text>
                  </g>
                )}
                
                {/* Interactive Section Regions */}
                <g id="section-interactions" style={{ pointerEvents: 'auto' }}>
                  {Object.entries(SECTIONS).map(([sectionId, section]) => {
                    const isHovered = hoveredSection === sectionId;
                    const isSelected = selectedSection === sectionId;
                    const hasTickets = sectionCounts[sectionId] > 0;
                    const isBehindStage = section.stageRelationship === 'behind_stage';
                    
                    return (
                      <g key={sectionId} data-testid={`section-${sectionId}`}>
                        <rect
                          x={section.bounds.x}
                          y={section.bounds.y}
                          width={section.bounds.width}
                          height={section.bounds.height}
                          fill={isHovered || isSelected ? COLORS.sectionHover : 'transparent'}
                          stroke={hasTickets ? COLORS.sectionOutline : 'transparent'}
                          strokeWidth={isHovered ? 3 : 2}
                          rx={4}
                          style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                          onClick={() => zoomToSection(sectionId)}
                          onMouseEnter={() => setHoveredSection(sectionId)}
                          onMouseLeave={() => setHoveredSection(null)}
                        />
                        
                        {/* Section label - show at certain zoom levels */}
                        {(zoomLevel === 'section' || zoomLevel === 'row' || isHovered) && (
                          <text
                            x={section.bounds.x + section.bounds.width / 2}
                            y={section.bounds.y + section.bounds.height / 2}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill={isHovered ? '#C9A962' : '#666'}
                            fontSize="20"
                            fontWeight="600"
                            style={{ pointerEvents: 'none' }}
                          >
                            {sectionId}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </g>
                
                {/* Ticket Count Badges (arena level only) */}
                {zoomLevel === 'arena' && (
                  <g id="section-counts">
                    {Object.entries(sectionCounts).map(([sectionId, count]) => {
                      const section = SECTIONS[sectionId];
                      if (!section || count === 0) return null;
                      
                      return (
                        <g key={`count-${sectionId}`} style={{ pointerEvents: 'none' }}>
                          <circle
                            cx={section.bounds.x + section.bounds.width / 2}
                            cy={section.bounds.y + section.bounds.height / 2}
                            r={25}
                            fill="#C9A962"
                          />
                          <text
                            x={section.bounds.x + section.bounds.width / 2}
                            y={section.bounds.y + section.bounds.height / 2 + 1}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill="white"
                            fontSize="14"
                            fontWeight="600"
                          >
                            {count}
                          </text>
                        </g>
                      );
                    })}
                  </g>
                )}
                
                {/* Individual Ticket Markers (section/row zoom only) */}
                {(zoomLevel === 'section' || zoomLevel === 'row' || zoomLevel === 'seat') && selectedSection && (
                  <g id="ticket-markers" style={{ pointerEvents: 'auto' }}>
                    {tickets
                      .filter(t => t.section === selectedSection || 
                        (selectedSection === 'golden-circle' && ['AA', 'BB', 'CC'].includes(t.section)))
                      .map(ticket => {
                        const section = SECTIONS[ticket.section];
                        if (!section) return null;
                        
                        // Calculate approximate position within section
                        // This will be replaced with exact coordinates from owner mapping
                        const offsetX = ((ticket.seat || 0) % 10 - 5) * 12;
                        const offsetY = ((ticket.row || 0) % 10 - 5) * 12;
                        const x = section.bounds.x + section.bounds.width / 2 + offsetX;
                        const y = section.bounds.y + section.bounds.height / 2 + offsetY;
                        
                        const isHovered = hoveredTicket === ticket.id;
                        
                        return (
                          <g
                            key={ticket.id}
                            data-testid={`marker-${ticket.section}-${ticket.row}-${ticket.seat}`}
                            onClick={() => setSelectedTicket(ticket)}
                            onMouseEnter={() => setHoveredTicket(ticket.id)}
                            onMouseLeave={() => setHoveredTicket(null)}
                            style={{ cursor: 'pointer' }}
                          >
                            <circle
                              cx={x}
                              cy={y}
                              r={isHovered ? 10 : 6}
                              fill={COLORS.ticketMarker}
                              stroke={COLORS.ticketMarkerBorder}
                              strokeWidth={2}
                              style={{ transition: 'r 0.15s' }}
                            />
                          </g>
                        );
                      })}
                  </g>
                )}
              </svg>
            </div>
          </TransformComponent>
        </TransformWrapper>
      </div>
      
      {/* ================================================================
          HOVER TOOLTIP
          ================================================================ */}
      {hoveredSection && SECTIONS[hoveredSection] && (
        <div style={{
          position: 'absolute',
          bottom: '80px',
          left: '20px',
          background: 'rgba(26, 26, 26, 0.95)',
          color: 'white',
          padding: '14px 18px',
          borderRadius: '10px',
          fontSize: '13px',
          maxWidth: '300px',
          zIndex: 200
        }}>
          <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '6px' }}>
            {SECTIONS[hoveredSection].label}
          </div>
          {SECTIONS[hoveredSection].historicalZone === 'golden-circle' && (
            <div style={{ color: '#C9A962', fontSize: '12px', marginBottom: '6px' }}>
              Golden Circle • Special contributor seating
            </div>
          )}
          <div style={{ color: '#C9A962' }}>
            {sectionCounts[hoveredSection] || 0} documented ticket{(sectionCounts[hoveredSection] || 0) !== 1 ? 's' : ''}
          </div>
          {layoutView === 'aloha-stage' && SECTIONS[hoveredSection].stageRelationship === 'behind_stage' && (
            <div style={{ color: '#ff9966', fontSize: '12px', marginTop: '6px' }}>
              Behind the reconstructed Aloha stage. Not used for concert audience.
            </div>
          )}
          {SECTIONS[hoveredSection].note && (
            <div style={{ color: '#aaa', fontSize: '11px', marginTop: '6px', fontStyle: 'italic' }}>
              {SECTIONS[hoveredSection].note}
            </div>
          )}
        </div>
      )}
      
      {/* ================================================================
          BOTTOM LEGEND & INFO
          ================================================================ */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '14px 20px',
        background: 'linear-gradient(to top, rgba(248,246,241,0.98), rgba(248,246,241,0))',
        zIndex: 100
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px' }}>
          {/* Legend */}
          <div style={{ display: 'flex', gap: '20px', fontSize: '12px', color: '#666' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#C9A962', border: '2px solid white' }} />
              <span>Known Ticket</span>
            </div>
            {showGoldenCircle && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', background: COLORS.goldenCircle, border: `2px dashed ${COLORS.goldenCircleBorder}`, borderRadius: '2px' }} />
                <span>Golden Circle Zone</span>
              </div>
            )}
            {layoutView === 'aloha-stage' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', background: COLORS.stageOverlay }} />
                <span>Stage Area</span>
              </div>
            )}
          </div>
          
          {/* Attribution */}
          <div style={{ fontSize: '11px', color: '#888', textAlign: 'right', maxWidth: '450px' }}>
            {layoutView === 'full-arena' 
              ? 'Neal S. Blaisdell Arena - 360 Stage Reference Layout' 
              : 'Aloha from Hawaii Research Reconstruction • Based on surviving tickets, photographs, and documentation'
            }
          </div>
        </div>
      </div>
      
      {/* ================================================================
          TICKET MODAL
          ================================================================ */}
      {selectedTicket && (
        <TicketDetailModal 
          ticket={selectedTicket} 
          onClose={() => setSelectedTicket(null)}
          layoutView={layoutView}
        />
      )}
      
      {/* Loading indicator */}
      {!imageLoaded && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(248,246,241,0.9)',
          zIndex: 300
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', color: '#888' }}>Loading seating chart...</div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TICKET DETAIL MODAL
// ============================================================================

const TicketDetailModal = ({ ticket, onClose, layoutView }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  
  const section = SECTIONS[ticket.section];
  const isGoldenCircle = section?.historicalZone === 'golden-circle';
  const isBehindStage = section?.stageRelationship === 'behind_stage';
  
  return (
    <div
      data-testid="ticket-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '24px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '85vh',
          overflow: 'auto'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee', background: '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '11px', color: '#C9A962', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '6px' }}>
                TICKET RECORD
              </div>
              <h2 style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', fontWeight: 500 }}>
                Section {ticket.section}, Row {ticket.row}, Seat {ticket.seat}
              </h2>
              
              {/* Historical Zone Badge */}
              {isGoldenCircle && (
                <div style={{
                  display: 'inline-block',
                  marginTop: '8px',
                  padding: '6px 12px',
                  background: 'rgba(201, 169, 98, 0.12)',
                  border: '1px solid rgba(201, 169, 98, 0.3)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#8a7842',
                  fontWeight: 600
                }}>
                  Golden Circle
                </div>
              )}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#888' }}>×</button>
          </div>
        </div>
        
        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Golden Circle explanation */}
          {isGoldenCircle && (
            <div style={{
              padding: '16px',
              background: 'rgba(255, 248, 230, 1)',
              border: '1px solid rgba(230, 217, 168, 1)',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#7a6832', marginBottom: '6px' }}>
                Historical Seating Zone: Golden Circle
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: '#8a7842', lineHeight: 1.6 }}>
                This printed seat assignment falls within the area identified by collection research as the Golden Circle—the 
                special contributor seating area nearest the Aloha performance. Approximately 200 of the event's largest 
                contributors received seats in this zone.
              </p>
            </div>
          )}
          
          {/* Behind Stage explanation */}
          {isBehindStage && layoutView === 'aloha-stage' && (
            <div style={{
              padding: '16px',
              background: '#f5f5f5',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#666', marginBottom: '6px' }}>
                Stage Relationship: Behind Stage
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: '#888', lineHeight: 1.6 }}>
                Section {ticket.section} was located behind the reconstructed Aloha stage. Tickets were printed 
                for these seats, but they were not used for the concert audience.
              </p>
            </div>
          )}
          
          {/* Ticket details grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <InfoField label="Registry Reference" value={ticket.registry_reference} />
            <InfoField label="Ticket Form" value={ticket.ticket_form?.replace(/_/g, ' ')} />
            <InfoField label="Ownership" value={ticket.ownership_status?.replace(/_/g, ' ')} />
            <InfoField label="Evidence Confidence" value={ticket.evidence_confidence?.replace(/_/g, ' ')} />
            <InfoField label="Issue Status" value={ticket.issue_status?.replace(/_/g, ' ')} />
            <InfoField label="Use Status" value={ticket.use_status?.replace(/_/g, ' ')} />
            <InfoField label="Graceland Custody" value={ticket.graceland_custody_status?.replace(/_/g, ' ')} />
            <InfoField label="Exhibition Status" value={ticket.graceland_exhibition_status?.replace(/_/g, ' ')} />
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoField = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '14px', color: value ? '#1a1a1a' : '#ccc', textTransform: 'capitalize' }}>{value || '—'}</div>
  </div>
);

export default InteractiveSeatingChart;
